import React, { useState, useEffect } from "react";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import LoginHeader from "./components/LoginHeader.js";
import SideNavigation from "./components/SideNavigation.js";
import config from "./config";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Button from "@mui/material/Button";
import WestIcon from "@mui/icons-material/West";
import EastIcon from "@mui/icons-material/East";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { BlobServiceClient } from "@azure/storage-blob";
import { useNavigate, useLocation } from "react-router-dom";
import { generateCsrdPdfBlob } from "./components/pdf.js";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import PDFErrorBoundary from "./components/PDFErrorBoundary";
import { loadReportDataForPdf } from "./utils/csrdAnswerUtils";

const steps = ["Start", "Materiality", "Module", "Input", "Report"];

export default function PreviewReport() {
  const username = localStorage.getItem("userName");
  const companyid = localStorage.getItem("companyid");
  const navigate = useNavigate();
  const organizationValue = localStorage.getItem("organization");
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [pdffileUrl, setPdfFileUrl] = useState(null);
  const [formData, setFormData] = useState({
    reportTitle: "",
    companyName: "",
    reportingYear: "",
    fileUrl: "",
    logoUrl: "",
  });
  const step1 = localStorage.getItem("step1");
  const reportName = JSON.parse(step1);
  const [showPDF, setShowPDF] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfError, setPdfError] = useState("");
  //const [reportData, setReportData] = useState(JSON.parse(localStorage.getItem("step4")) || []);
  // Function to remove duplicates
  const removeDuplicates = (data) => {
    const uniqueQuestions = new Map();
    data.forEach((item) => {
      const key = item?.id ?? (item?.question ? item.question.trim() : undefined);
      if (key == null) return;
      uniqueQuestions.set(key, item);
    });
    return Array.from(uniqueQuestions.values());
  };
  const loadReportDataFromStep4 = () => loadReportDataForPdf();

  const location = useLocation();
  const [reportData, setReportData] = useState(() => loadReportDataFromStep4());

  // Refresh report data from step4 whenever Preview page is shown so PDF always has latest saved table data
  useEffect(() => {
    setReportData(loadReportDataFromStep4());
  }, [location.pathname]);

  useEffect(() => {
    const refresh = () => setReportData(loadReportDataFromStep4());
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, []);

  useEffect(() => {
    return () => {
      if (pdffileUrl) {
        URL.revokeObjectURL(pdffileUrl);
      }
    };
  }, [pdffileUrl]);

  const [fileUploaded, setFileUploaded] = useState(false);
  const hash = localStorage.getItem("hash");
  const validateImageFile = (file) => {
    const allowedFormats = ["image/jpeg", "image/jpg", "image/png"];
    const maxFileSize = 2 * 1024 * 1024; // 2 MB

    // Check file format
    if (!allowedFormats.includes(file.type)) {
      setErrors({
        ...errors,
        fileUrl: "Invalid file format. Only JPG, JPEG, and PNG files are allowed.",
      });
      return false;
    }

    // Check file size
    if (file.size > maxFileSize) {
      setErrors({
        ...errors,
        fileUrl: "File size exceeds 2 MB. Please upload a smaller file.",
      });
      return false;
    }

    // Clear errors if validation passes
    setErrors({
      ...errors,
      fileUrl: "",
    });
    return true;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && validateImageFile(file)) {
      setFormData({
        ...formData,
        fileUrl: URL.createObjectURL(file),
        logoUrl: URL.createObjectURL(file),
      });
      setSelectedFile(file);
      setFileUrl(URL.createObjectURL(file));
      uploadFile("ecoprismfilecontainer", companyid, file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file && validateImageFile(file)) {
      setFormData({
        ...formData,
        fileUrl: URL.createObjectURL(file),
        logoUrl: URL.createObjectURL(file),
      });
      setSelectedFile(file);
      setFileUrl(URL.createObjectURL(file));
      uploadFile("ecoprismfilecontainer", companyid, file);
      setFileUploaded(true);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsSidebarOpen(false); // Close sidebar on mobile
      } else {
        setIsSidebarOpen(true); // Open sidebar on larger screens
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  async function uploadJsonFile(containerName, folderName, fileName, jsonData) {
    try {
      let res = await fetch(`${process.env.REACT_APP_BASE_API_URL}GetSASToken`, {
        method: "GET",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
      });

      if (res.status === 200) {
        const responseJson = await res.json();
        const connectionString = responseJson.data;

        const blobServiceClient = new BlobServiceClient(connectionString);
        const containerClient = blobServiceClient.getContainerClient(containerName);
        const blobName = `${folderName}/${fileName}.json`;
        const blobClient = containerClient.getBlobClient(blobName);
        const blockBlobClient = blobClient.getBlockBlobClient();
        const arrayBuffer = new TextEncoder().encode(JSON.stringify(jsonData));
        const uploadResponse = await blockBlobClient.uploadBrowserData(arrayBuffer.buffer, {
          blobHTTPHeaders: { blobContentType: "application/json" },
          blockSize: 4 * 1024 * 1024,
          concurrency: 20,
          onProgress: (ev) => console.log(ev.loadedBytes),
        });
        if (uploadResponse) {
          localStorage.setItem("bloburl", blobClient.url);
        }
      } else {
        console.error("Failed to fetch SAS token.");
      }
    } catch (error) {
      console.error("Error uploading JSON file:", error);
    }
  }

  async function uploadFile(containerName, folderName, file) {
    try {
      let res = await fetch(`${config.apiBaseUrl}/GetSASToken`, {
        method: "GET",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
      });

      if (res.status === 200) {
        const responseJson = await res.json();
        const connectionString = responseJson.data;

        const blobServiceClient = new BlobServiceClient(connectionString);
        const containerClient = blobServiceClient.getContainerClient(containerName);
        const blobName = `${folderName}/${file.name}`;
        const blobClient = containerClient.getBlobClient(blobName);
        const blockBlobClient = blobClient.getBlockBlobClient();
        const uploadResponse = await blockBlobClient.uploadBrowserData(file, {
          blobHTTPHeaders: { blobContentType: file.type },
          blockSize: 4 * 1024 * 1024,
          concurrency: 20,
          onProgress: (ev) => console.log(ev.loadedBytes),
        });
        if (uploadResponse) {
          console.log(`File "${file.name}" uploaded successfully.`);
          console.log(`Blob URL: ${blobClient.url}`);
          localStorage.setItem("bloburl", blobClient.url);
          localStorage.setItem("logo", file.name);
        }
      } else {
        console.error("Failed to fetch SAS token.");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  }

  const validateForm = () => {
    const newErrors = {};
    if (!formData.reportTitle) newErrors.reportTitle = "Report Title is required";
    if (!formData.companyName) newErrors.companyName = "Company Name is required";
    const currentYear = new Date().getFullYear();
    if (!formData.reportingYear) {
      newErrors.reportingYear = "Reporting Year is required";
    } else if (!/^\d{4}$/.test(formData.reportingYear)) {
      newErrors.reportingYear = "Reporting Year must be a 4-digit number";
    } else {
      const numericYear = Number(formData.reportingYear);
      if (numericYear < 1900 || numericYear > currentYear + 1) {
        newErrors.reportingYear = `Reporting Year must be between 1900 and ${currentYear + 1}`;
      }
    }
    if (!formData.fileUrl) newErrors.fileUrl = "Logo is required";

    setErrors(newErrors);
    return newErrors;
  };

  const handleSave = async () => {
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      return;
    }
    setLoading(true);
    setPdfError("");

    try {
      const step1 = localStorage.getItem("step1");
      const step2 = localStorage.getItem("step2");
      const step3 = localStorage.getItem("step3");
      const step4 = localStorage.getItem("step4");
      const answers = localStorage.getItem("answers");
      const answers1 = localStorage.getItem("answers1");
      const tableanswers = localStorage.getItem("tableAnswers");
      const selectedOptions = localStorage.getItem("selectedOptions");
      const logo = localStorage.getItem("logo");
      const parseData = JSON.parse(step1);

      const rdata = {
        name: parseData.name,
        timeline: parseData.timeline,
        legalEntity: parseData.legalEntity,
        startDate: parseData.startDate,
        endDate: parseData.endDate,
        materiality: JSON.parse(step2 || "[]"),
        reportingModule: JSON.parse(step3 || "[]"),
        generateReport: JSON.parse(step4 || "[]"),
        answers: JSON.parse(answers || "{}"),
        answers1: JSON.parse(answers1 || "{}"),
        tableAnswers: tableanswers ? JSON.parse(tableanswers) : {},
        selectedOptions: JSON.parse(selectedOptions || "{}"),
        logo: logo,
        reportTitle: formData.reportTitle,
        companyName: formData.companyName,
        reportingYear: formData.reportingYear,
      };

      await uploadJsonFile("ecoprismfilecontainer", companyid, hash, rdata);
    } catch (error) {
      console.error("Failed to save report data before preview:", error);
      setPdfError("Could not save report data. Please complete earlier steps and try again.");
      setLoading(false);
      return;
    }

    setIsGeneratingPdf(true);
    try {
      const freshReportData = loadReportDataFromStep4();
      setReportData(freshReportData);
      const pdfBlob = await generateCsrdPdfBlob({
        reportData: freshReportData,
        reportTitle: formData.reportTitle,
        reportingYear: formData.reportingYear,
        companyLogo: fileUrl,
      });
      if (pdffileUrl) {
        URL.revokeObjectURL(pdffileUrl);
      }
      setPdfFileUrl(URL.createObjectURL(pdfBlob));
      setShowPDF(true);
    } catch (e) {
      console.error("Failed to generate CSRD PDF:", e);
      setShowPDF(false);
      setPdfError("Failed to generate the PDF preview. Please refresh the page and try again.");
    } finally {
      setIsGeneratingPdf(false);
      setLoading(false);
    }
  };

  const handleDownloadGeneratedPdf = () => {
    if (!pdffileUrl) return;
    const rawTitle = formData.reportTitle || "Report";
    const safeTitle =
      String(rawTitle)
        .trim()
        .replace(/[\\/:*?"<>|]/g, "-")
        .replace(/\s+/g, " ")
        .slice(0, 120) || "Report";
    const a = document.createElement("a");
    a.href = pdffileUrl;
    a.download = `${safeTitle}.pdf`;
    a.click();
  };

  const handleRemoveImage = () => {
    setFileUrl(null); // Assuming you are using useState to manage fileUrl
    setFileUploaded(false);
  };

  return (
    <div id="wrapper">
      <div>
        {loading && (
          <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </div>
      <LoginHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />
      <SideNavigation isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div id="page-content-wrapper" className={isSidebarOpen ? "page-content-wrapper-pd-0" : "page-wrapper"}>
        <div className="container formpd">
          <div className="">
            <div className="row">
              <div className="steps__containt-hader col-md-6">
                <h3 className="title">{reportName.name}</h3>
                <div role="presentation">
                  <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" href="/standards">
                      Reports
                    </Link>
                    <Link underline="hover" color="inherit" href="/standards">
                      Standards & Frameworks
                    </Link>
                    <Link underline="hover" color="inherit" href={`/editcsrd?file=${hash}`}>
                      CSRD
                    </Link>
                    <Link underline="hover" color="inherit" href="/editgeneratereport">
                      {reportName.name}
                    </Link>
                    <Typography color="text.primary">Preview Report</Typography>
                  </Breadcrumbs>
                </div>
              </div>
              <div className="stepper-box col-md-6">
                <Stepper activeStep={4} alternativeLabel className="csrd-stepper">
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel className="step-name">{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </div>
            </div>
          </div>
          <div>
            <div className="button-box d-flex justify-content-end">
              <Button onClick={() => navigate("/editgeneratereport")} variant="outlined" className="module-btn" size="medium" sx={{ marginRight: "15px" }}>
                <WestIcon fontSize="small" sx={{ marginRight: "4px" }} />
                Input
              </Button>
              <Button
                onClick={() => {
                  const step1 = localStorage.getItem("step1"); // <-- Add this line
                  if (step1) {
                    const parsed = JSON.parse(step1);
                    const reportName = parsed.name;
                    const completionDate = parsed.timeline1 || parsed.timeline;
                    localStorage.setItem("reportStatusData", JSON.stringify({ reportName, completionDate }));
                    const statusFile = hash ? `${hash}.json` : localStorage.getItem("statusReportFile") || "";
                    localStorage.setItem("statusReportFile", statusFile);
                    window.location.href = `/reportdashboard?file=${encodeURIComponent(statusFile)}`;
                  }
                }}
                variant="outlined"
                className="module-btn"
                size="medium"
                sx={{ marginRight: "15px" }}
              >
                <img src="/assets/img/reports/Status.svg" style={{ marginRight: "4px" }} />
                Status
              </Button>
              {/* <Button onClick={handleSave} variant="outlined" className="module-btn" size="medium">
                Generate Report
                <EastIcon fontSize="small" sx={{ marginLeft: "4px" }} />
              </Button> */}
            </div>
            <div className="col-md-12 mt-4 pd-0">
              <form action="">
                <div className="form-row">
                  {pdfError && (
                    <div className="alert alert-danger" role="alert">
                      {pdfError}
                    </div>
                  )}
                </div>
                <div className="form-row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="formlable" htmlFor="reportTitle">
                        Report Title
                      </label>
                      <input
                        type="text"
                        required
                        className={`form-control wd-85 ${errors.reportTitle ? "is-invalid" : ""}`}
                        name="reportTitle"
                        placeholder="Enter the report title"
                        value={formData.reportTitle}
                        onChange={(e) => {
                          const input = e.target.value;

                          if (input.length <= 20) {
                            handleFormChange(e);
                          } else {
                            setErrors((prevErrors) => ({
                              ...prevErrors,
                              reportTitle: "Report title cannot exceed 20 characters",
                            }));
                          }
                        }}
                      />
                      {errors.reportTitle && <div className="text-danger">{errors.reportTitle}</div>}
                    </div>
                    <div className="form-group">
                      <label className="formlable" htmlFor="companyName">
                        Company Name
                      </label>
                      <input type="text" required className={`form-control wd-85 ${errors.companyName ? "is-invalid" : ""}`} name="companyName" placeholder="Enter Company name" value={formData.companyName} onChange={handleFormChange} />
                      {errors.companyName && <div className="text-danger">{errors.companyName}</div>}
                    </div>
                    <div className="form-group">
                      <label className="formlable" htmlFor="reportingYear">
                        Reporting Year
                      </label>
                      <input
                        type="text"
                        required
                        className={`form-control wd-85 ${errors.reportingYear ? "is-invalid" : ""}`}
                        name="reportingYear"
                        placeholder="Enter year"
                        value={formData.reportingYear}
                        inputMode="numeric"
                        pattern="\d{4}"
                        maxLength={4}
                        onChange={(e) => {
                          const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 4);
                          setFormData((prev) => ({ ...prev, reportingYear: onlyDigits }));
                          setErrors((prev) => ({ ...prev, reportingYear: "" }));
                        }}
                      />
                      {errors.reportingYear && <div className="text-danger">{errors.reportingYear}</div>}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="formlable" htmlFor="companyLogo">
                        Company Logo
                      </label>
                      <div className="preview-report-dropzone">
                        <input type="file" id="companyLogo" accept=".jpg,.jpeg,.png" name="fileUrl" style={{ display: "none" }} onChange={handleFileChange} />
                        <label
                          htmlFor="companyLogo"
                          className={`preview-report-upload-box ${fileUrl ? "has-file" : ""}`}
                          onDrop={handleDrop}
                          onDragOver={handleDragOver}
                          onDragEnter={handleDragEnter}
                          onDragLeave={handleDragLeave}
                        >
                          {!fileUrl ? (
                            <>
                              <div className="file-icon">
                                <img className="icon" src="/assets/img/import.png" alt="" />
                              </div>
                              <div className="dropzone-title">
                                <span className="browse">Browse</span> or drag and drop
                              </div>
                            </>
                          ) : (
                            <div className="preview-report-file-preview">
                              <img src={fileUrl} alt="Company Logo" />
                              <IconButton
                                aria-label="remove"
                                className="preview-report-remove-btn"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleRemoveImage();
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </div>
                          )}
                        </label>
                        <p className="preview-report-upload-hint">Accepted formats: JPG, JPEG, PNG | Max file size: 2 MB</p>
                        {errors.fileUrl && <div className="text-danger">{errors.fileUrl}</div>}
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
            <div className="row justify-content-end pt-3">
              <div className="col-auto">
                {!showPDF && (
                  <Button onClick={handleSave} disabled={loading || isGeneratingPdf} className="next-button" variant="contained" size="medium" sx={{ width: "180px !important", padding: "0 20px !important", marginBottom: "30px" }}>
                    {loading || isGeneratingPdf ? (
                      <>
                        <CircularProgress size={18} color="inherit" sx={{ marginRight: "8px" }} />
                        Loading...
                      </>
                    ) : (
                      "Preview Report"
                    )}
                  </Button>
                )}
                {showPDF && pdffileUrl && (
                  <PDFErrorBoundary>
                    <Button
                      onClick={handleDownloadGeneratedPdf}
                      className="next-button"
                      variant="contained"
                      size="medium"
                      sx={{
                        width: "200px !important",
                        padding: "0 20px !important",
                        marginBottom: "30px",
                        marginLeft: "10px",
                      }}
                    >
                      Download Report
                    </Button>
                  </PDFErrorBoundary>
                )}
              </div>
            </div>
            {showPDF && pdffileUrl && (
              <PDFErrorBoundary>
                <div style={{ width: "100%", height: "100vh" }}>
                  <iframe title="Report PDF preview" src={pdffileUrl} style={{ width: "100%", height: "100%", border: "none" }} />
                </div>
              </PDFErrorBoundary>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
